import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { Menu, MenuItem } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { getContractTemplate, getContractCategories } from '../../../api/contract';
import { stripHtml } from '../../../utils/methods';
import { CLAUSE_FIELDS } from './ElectronicContract';
import {
  CreateContainer,
  TopRow,
  BackGroup,
  BackIconButton,
  BackText,
  BodyRow,
  LeftColumn,
  RightColumn,
  StepRow,
  StepTab,
  StepBadge,
  StepLabel,
  Card,
  CardTitle,
  FieldList,
  Field,
  FieldLabelRow,
  FieldLabel,
  TextField,
  SelectBox,
  Placeholder,
  MiniToggle,
  OptionThumb,
  InfoBox,
  InfoText,
  TermHeaderRow,
  TermLabel,
  TermBox,
  TermInput,
  FooterRow,
  CancelButton,
  NextButton,
} from './ElectronicContractCreateStyled';

const ElectronicContractCreate = ({ editingId, form, setForm, clauseContentMap, logoSeal, onBack, onNext }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const token = useSelector(state => state.user.user?.token);

  // 드롭다운 anchor (카테고리 / 직인 / 로고)
  const [categoryAnchor, setCategoryAnchor] = useState(null);
  const [sealAnchor, setSealAnchor] = useState(null);
  const [logoAnchor, setLogoAnchor] = useState(null);
  // 약관 표시 텍스트는 form.termDrafts 로 관리 (미리보기에도 전달)
  const termDrafts = form.termDrafts || {};
  // 미입력 필드 에러 표시 상태. 키: 'name' | 'category' | 'seal' | 'logo' | clauseType
  const [errorFields, setErrorFields] = useState({});

  // 지점에 등록된 직인/로고 목록 (1:N)
  const seals = logoSeal?.seals || [];
  const logos = logoSeal?.logos || [];
  // 현재 선택된 직인/로고 (드롭다운 표시 라벨용)
  const selectedSeal = seals.find(s => s.id === form.sealId);
  const selectedLogo = logos.find(l => l.id === form.logoId);

  // 수정 진입 시 기존 계약서 상세를 불러와 폼을 채운다.
  const { data: detail } = useQuery({
    queryKey: ['contractTemplate', editingId],
    queryFn: () => getContractTemplate(editingId, token),
    enabled: !!editingId && !!token,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['contractCategories'],
    queryFn: () => getContractCategories(token),
    enabled: !!token,
    staleTime: 1000 * 60 * 30,
  });

  useEffect(() => {
    if (!detail) return;
    const sortedClauses = [...(detail.clauses || [])]
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map(clause => clause.clauseType);
    // 상세에 담긴 조항 내용도 표시용으로 채워둔다.
    const drafts = {};
    (detail.clauses || []).forEach(clause => {
      drafts[clause.clauseType] = stripHtml(clause.content);
    });
    setForm(prev => ({
      ...prev,
      name: detail.name || '',
      category: detail.category || '',
      clauses: sortedClauses,
      logoId: detail.logo?.id ?? null,
      sealId: detail.seal?.id ?? null,
      showLogo: !!detail.logo,
      showSeal: !!detail.seal,
      termDrafts: drafts,
    }));
  }, [detail, setForm]);

  // 해당 필드의 에러 표시 제거
  const clearError = field =>
    setErrorFields(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const updateName = e => {
    setForm(prev => ({ ...prev, name: e.target.value }));
    if (e.target.value.trim()) clearError('name');
  };

  const selectCategory = value => {
    setForm(prev => ({ ...prev, category: value }));
    setCategoryAnchor(null);
    clearError('category');
  };

  const selectSeal = id => {
    setForm(prev => ({ ...prev, sealId: id }));
    setSealAnchor(null);
    clearError('seal');
  };

  const selectLogo = id => {
    setForm(prev => ({ ...prev, logoId: id }));
    setLogoAnchor(null);
    clearError('logo');
  };

  // 직인/로고 표시 토글. 끄면 더 이상 필수가 아니므로 에러 해제.
  const toggleFlag = key => {
    setForm(prev => ({ ...prev, [key]: !prev[key] }));
    if (key === 'showSeal') clearError('seal');
    if (key === 'showLogo') clearError('logo');
  };

  // 약관 토글 ON → 지점 약관 자동 로드 / OFF → 제거
  const toggleClause = clauseType => {
    const isActive = form.clauses.includes(clauseType);
    if (isActive) {
      // OFF: 조항 제거 + 내용 제거
      setForm(prev => ({
        ...prev,
        clauses: prev.clauses.filter(c => c !== clauseType),
        termDrafts: { ...prev.termDrafts, [clauseType]: '' },
      }));
    } else {
      // ON: 조항 추가 + 지점 약관 자동 로드
      const content = stripHtml(clauseContentMap[clauseType]);
      setForm(prev => ({
        ...prev,
        clauses: [...prev.clauses, clauseType],
        termDrafts: { ...prev.termDrafts, [clauseType]: content },
      }));
    }
    clearError(clauseType);
  };

  // 다음 단계로: 계약서 명/카테고리/직인·로고(토글 ON)/약관 내용을 1차 검증한다.
  const handleNext = () => {
    const errors = {};
    let firstMessage = '';
    const flag = (condition, field, message) => {
      if (!condition) return;
      errors[field] = true;
      if (!firstMessage) firstMessage = message;
    };

    flag(!form.name.trim(), 'name', t('계약서 명을 입력해 주세요.'));
    flag(!form.category, 'category', t('카테고리를 선택해 주세요.'));
    flag(form.showSeal && !form.sealId, 'seal', t('서명 또는 직인을 선택해 주세요.'));
    flag(form.showLogo && !form.logoId, 'logo', t('로고를 선택해 주세요.'));
    // 토글이 켜진 약관 중 지점에 등록된 내용이 없는 곳
    form.clauses
      .filter(clauseType => !(termDrafts[clauseType] || '').trim())
      .forEach(clauseType => {
        const label = CLAUSE_FIELDS.find(f => f.clauseType === clauseType)?.label || '';
        flag(true, clauseType, t('센터 관리 > 센터 목록 > 센터 이용약관에서 내용을 입력해 주세요.'));
      });

    if (Object.keys(errors).length > 0) {
      setErrorFields(errors);
      toast.error(firstMessage);
      return;
    }
    setErrorFields({});
    onNext();
  };

  return (
    <CreateContainer>
      {/* 스텝 + 목록으로 */}
      <TopRow>
        <StepRow>
          <StepTab first active>
            <StepBadge active>1</StepBadge>
            <StepLabel active>{t('계약서 정보')}</StepLabel>
          </StepTab>
          <StepTab>
            <StepBadge>2</StepBadge>
            <StepLabel>{t('미리보기')}</StepLabel>
          </StepTab>
        </StepRow>

        <BackGroup onClick={onBack}>
          <BackIconButton aria-label="back">
            <ArrowBackIcon />
          </BackIconButton>
          <BackText>{t('목록으로')}</BackText>
        </BackGroup>
      </TopRow>

      <BodyRow>
        {/* 왼쪽: 기본정보 + 안내 */}
        <LeftColumn>
          <Card>
            <CardTitle>{t('기본정보')}</CardTitle>
            <FieldList>
              <Field>
                <FieldLabel>{t('계약서 명')}</FieldLabel>
                <TextField
                  hasError={!!errorFields.name}
                  placeholder={t('계약서 명을 입력해 주세요.')}
                  value={form.name}
                  onChange={updateName}
                />
              </Field>

              <Field>
                <FieldLabel>{t('카테고리')}</FieldLabel>
                <SelectBox hasError={!!errorFields.category} onClick={e => setCategoryAnchor(e.currentTarget)}>
                  <Placeholder style={form.category ? { color: '#141414' } : undefined}>
                    {form.category ? (categories.find(c => c.value === form.category)?.label || form.category) : t('카테고리를 선택해 주세요.')}
                  </Placeholder>
                  <ArrowDropDownIcon sx={{ color: '#141414' }} />
                </SelectBox>
                <Menu anchorEl={categoryAnchor} open={!!categoryAnchor} onClose={() => setCategoryAnchor(null)}>
                  {categories.map(opt => (
                    <MenuItem
                      key={opt.value}
                      selected={form.category === opt.value}
                      onClick={() => selectCategory(opt.value)}>
                      {t(opt.label)}
                    </MenuItem>
                  ))}
                </Menu>
              </Field>

              <Field>
                <FieldLabelRow>
                  <FieldLabel>{t('서명 또는 직인')}</FieldLabel>
                  <MiniToggle checked={form.showSeal} onChange={() => toggleFlag('showSeal')} />
                </FieldLabelRow>
                <SelectBox
                  disabled={!form.showSeal}
                  hasError={!!errorFields.seal}
                  onClick={e => form.showSeal && setSealAnchor(e.currentTarget)}>
                  <Placeholder style={selectedSeal ? { color: '#141414' } : undefined}>
                    {selectedSeal ? selectedSeal.title || t('직인') : t('서명 또는 직인을 선택해 주세요.')}
                  </Placeholder>
                  <ArrowDropDownIcon sx={{ color: form.showSeal ? '#141414' : '#A6A6A6' }} />
                </SelectBox>
                <Menu anchorEl={sealAnchor} open={!!sealAnchor} onClose={() => setSealAnchor(null)}>
                  {seals.length === 0 ? (
                    <MenuItem disabled>{t('등록된 직인이 없습니다.')}</MenuItem>
                  ) : (
                    seals.map((s, i) => (
                      <MenuItem key={s.id} selected={form.sealId === s.id} onClick={() => selectSeal(s.id)}>
                        {s.imageUrl && <OptionThumb src={s.imageUrl} alt="" />}
                        {s.title || `${t('직인')} ${i + 1}`}
                      </MenuItem>
                    ))
                  )}
                </Menu>
              </Field>

              <Field>
                <FieldLabelRow>
                  <FieldLabel>{t('로고')}</FieldLabel>
                  <MiniToggle checked={form.showLogo} onChange={() => toggleFlag('showLogo')} />
                </FieldLabelRow>
                <SelectBox
                  disabled={!form.showLogo}
                  hasError={!!errorFields.logo}
                  onClick={e => form.showLogo && setLogoAnchor(e.currentTarget)}>
                  <Placeholder style={selectedLogo ? { color: '#141414' } : undefined}>
                    {selectedLogo ? selectedLogo.title || t('로고') : t('로고를 선택해 주세요.')}
                  </Placeholder>
                  <ArrowDropDownIcon sx={{ color: form.showLogo ? '#141414' : '#A6A6A6' }} />
                </SelectBox>
                <Menu anchorEl={logoAnchor} open={!!logoAnchor} onClose={() => setLogoAnchor(null)}>
                  {logos.length === 0 ? (
                    <MenuItem disabled>{t('등록된 로고가 없습니다.')}</MenuItem>
                  ) : (
                    logos.map((l, i) => (
                      <MenuItem key={l.id} selected={form.logoId === l.id} onClick={() => selectLogo(l.id)}>
                        {l.imageUrl && <OptionThumb src={l.imageUrl} alt="" />}
                        {l.title || `${t('로고')} ${i + 1}`}
                      </MenuItem>
                    ))
                  )}
                </Menu>
              </Field>
            </FieldList>
          </Card>

          <InfoBox>
            <HelpOutlineIcon sx={{ fontSize: 32, color: '#737373' }} />
            <InfoText>
              {t('같은 카테고리의 계약서를 여러 개 생성할 수 있지만, 카테고리별로 한 개씩만 활성화가 가능합니다.')}
            </InfoText>
          </InfoBox>
        </LeftColumn>

        {/* 오른쪽: 약관내용 */}
        <RightColumn>
          <Card>
            <CardTitle>
              {t('약관내용')}
              <span style={{ fontSize: '12px', fontWeight: 400, color: '#737373', marginLeft: '8px' }}>
                {t('*계약서에 해당 내용을 넣을건지 on/off 할 수 있으며, 각 약관들의 내용은 사이드메뉴에서')}
                <strong style={{ fontWeight: 700 }}>{t(' 센터 관리 > 센터 이용약관')}</strong>
                {t('에서 설정할 수 있습니다.')}
              </span>
            </CardTitle>
            <FieldList>
              {CLAUSE_FIELDS.map(term => {
                // 토글 OFF(미선택)면 추가 안 됨 + 입력/불러오기 모두 비활성화
                const isClauseActive = form.clauses.includes(term.clauseType);
                return (
                  <Field key={term.clauseType}>
                    <TermHeaderRow>
                      <TermLabel>{t(term.label)}</TermLabel>
                      <MiniToggle checked={isClauseActive} onChange={() => toggleClause(term.clauseType)} />
                    </TermHeaderRow>
                    <TermBox disabled={!isClauseActive} hasError={!!errorFields[term.clauseType]}>
                      <TermInput
                        multiline
                        disabled
                        placeholder={t(term.placeholder)}
                        value={termDrafts[term.clauseType] || ''}
                      />
                    </TermBox>
                  </Field>
                );
              })}
            </FieldList>
          </Card>
        </RightColumn>
      </BodyRow>

      {/* 푸터 */}
      <FooterRow>
        <CancelButton onClick={onBack}>{t('취소')}</CancelButton>
        <NextButton onClick={handleNext}>{t('다음')}</NextButton>
      </FooterRow>
    </CreateContainer>
  );
};

export default ElectronicContractCreate;
